import { useEffect, useRef, useState } from 'react';
import type { FormSchema } from '@hcc/types';
import { useSaveFormDraft } from '../../../hooks/useOnboardingForms';

export type BuilderSaveState = 'idle' | 'saving' | 'saved' | 'error';

interface BuilderAutosaveInput {
  formId: string;
  /** Fallback when the title box is blank, so the draft always has a name. */
  fallbackTitle: string;
  title: string;
  description: string;
  schema: FormSchema;
}

/**
 * Debounced draft autosave for the form builder, with a best-effort flush on
 * unmount (route nav that bypasses Back) and a publish guard so a just-
 * published form's cleared server draft isn't resurrected.
 */
export function useBuilderAutosave({
  formId,
  fallbackTitle,
  title,
  description,
  schema,
}: BuilderAutosaveInput) {
  const saveDraft = useSaveFormDraft();
  const [saveState, setSaveState] = useState<BuilderSaveState>('idle');
  const dirtyRef = useRef(false);
  const publishedRef = useRef(false);
  const firstRun = useRef(true);
  // Latest values for the unmount flush.
  const latest = useRef({ title, description, schema });
  latest.current = { title, description, schema };

  async function persistDraft() {
    await saveDraft.mutateAsync({
      id: formId,
      title: latest.current.title.trim() || fallbackTitle,
      description: latest.current.description.trim() || undefined,
      schema: latest.current.schema,
    });
  }
  const persistRef = useRef(persistDraft);
  persistRef.current = persistDraft;

  // Debounced autosave whenever the working state changes.
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    dirtyRef.current = true;
    setSaveState('saving');
    const t = setTimeout(async () => {
      try {
        await persistRef.current();
        dirtyRef.current = false;
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, 700);
    return () => clearTimeout(t);
  }, [title, description, schema]);

  // Best-effort save on unmount when there are unsaved edits and we didn't
  // just publish.
  useEffect(() => {
    return () => {
      if (dirtyRef.current && !publishedRef.current) {
        void persistRef.current().catch(() => {});
      }
    };
  }, []);

  /** Persist now (Save draft button / Back), updating the status line. */
  async function saveNow(): Promise<boolean> {
    try {
      await persistDraft();
      dirtyRef.current = false;
      setSaveState('saved');
      return true;
    } catch {
      setSaveState('error');
      return false;
    }
  }

  /** Flush any dirty state, then run the navigation. Errors leave the unmount flush to retry. */
  async function flushThen(nav: () => void) {
    if (dirtyRef.current && !publishedRef.current) {
      try {
        await persistDraft();
        dirtyRef.current = false;
      } catch {
        /* leave it; unmount flush will retry */
      }
    }
    nav();
  }

  /** Publishing clears the server draft; don't let the unmount flush resurrect it. */
  function markPublished() {
    publishedRef.current = true;
    dirtyRef.current = false;
  }

  return { saveState, saveNow, flushThen, markPublished, savePending: saveDraft.isPending };
}
